/**
 * Permission Utilities
 * Helper functions for checking user permissions
 */
import { ROLES } from '../constants/roles';
import PERMISSIONS, { DEFAULT_ROLE_PERMISSIONS } from '../constants/permissions';

/**
 * Check if user has a specific permission
 * @param {Object} user - User object with permissions
 * @param {string} permission - Permission to check
 * @returns {boolean} True if user has permission
 */
export const hasPermission = (user, permission) => {
  if (!user) return false;

  // Admin has all permissions
  if (user.role === ROLES.ADMIN) return true;

  // Check explicit user permissions
  if (user.permissions && user.permissions.includes(permission)) {
    return true;
  }

  // Check role-based permissions
  const rolePermissions = DEFAULT_ROLE_PERMISSIONS[user.role] || [];
  return rolePermissions.includes(permission);
};

/**
 * Check if user has any of the specified permissions
 * @param {Object} user - User object
 * @param {string[]} permissions - Array of permissions
 * @returns {boolean} True if user has any permission
 */
export const hasAnyPermission = (user, permissions) => {
  if (!user || !permissions || !permissions.length) return false;
  return permissions.some((permission) => hasPermission(user, permission));
};

/**
 * Check if user has all specified permissions
 * @param {Object} user - User object
 * @param {string[]} permissions - Array of permissions
 * @returns {boolean} True if user has all permissions
 */
export const hasAllPermissions = (user, permissions) => {
  if (!user || !permissions || !permissions.length) return false;
  return permissions.every((permission) => hasPermission(user, permission));
};

/**
 * Get all permissions for a user
 * @param {Object} user - User object
 * @returns {string[]} Array of permissions
 */
export const getUserPermissions = (user) => {
  if (!user) return [];

  if (user.role === ROLES.ADMIN) {
    return Object.values(PERMISSIONS);
  }

  const rolePermissions = DEFAULT_ROLE_PERMISSIONS[user.role] || [];
  const userPermissions = user.permissions || [];

  return [...new Set([...rolePermissions, ...userPermissions])];
};

/**
 * Check if user has dashboard access
 * @param {Object} user - User object
 * @returns {boolean} True if can view dashboard
 */
export const canViewDashboard = (user) => {
  return hasPermission(user, PERMISSIONS.DASHBOARD_VIEW);
};

/**
 * Check if user can manage members
 * @param {Object} user - User object
 * @returns {boolean} True if can manage
 */
export const canManageMembers = (user) => {
  return hasPermission(user, PERMISSIONS.MEMBERS_CREATE);
};

/**
 * Check if user can approve loans
 * @param {Object} user - User object
 * @returns {boolean} True if can approve
 */
export const canApproveLoans = (user) => {
  return hasPermission(user, PERMISSIONS.LOANS_APPROVE);
};

/**
 * Check if user can disburse loans
 * @param {Object} user - User object
 * @returns {boolean} True if can disburse
 */
export const canDisburseLoans = (user) => {
  return hasPermission(user, PERMISSIONS.LOANS_DISBURSE);
};

/**
 * Check if user can manage financial transactions
 * @param {Object} user - User object
 * @returns {boolean} True if can manage
 */
export const canManageAccounting = (user) => {
  return hasPermission(user, PERMISSIONS.ACCOUNTING_CREATE_TRANSACTION);
};

/**
 * Check if user is admin
 * @param {Object} user - User object
 * @returns {boolean} True if admin
 */
export const isAdmin = (user) => {
  return user?.role === ROLES.ADMIN;
};

/**
 * Check if user is manager
 * @param {Object} user - User object
 * @returns {boolean} True if manager
 */
export const isManager = (user) => {
  return user?.role === ROLES.MANAGER;
};

/**
 * Check if user is accountant
 * @param {Object} user - User object
 * @returns {boolean} True if accountant
 */
export const isAccountant = (user) => {
  return user?.role === ROLES.ACCOUNTANT;
};

/**
 * Check if user is loan officer
 * @param {Object} user - User object
 * @returns {boolean} True if loan officer
 */
export const isLoanOfficer = (user) => {
  return user?.role === ROLES.LOAN_OFFICER;
};

/**
 * Check if user is teller
 * @param {Object} user - User object
 * @returns {boolean} True if teller
 */
export const isTeller = (user) => {
  return user?.role === ROLES.TELLER;
};

/**
 * Check if user is member
 * @param {Object} user - User object
 * @returns {boolean} True if member
 */
export const isMember = (user) => {
  return user?.role === ROLES.MEMBER;
};

/**
 * Check if user can edit a resource
 * @param {Object} user - User object
 * @param {Object} resource - Resource with createdBy
 * @returns {boolean} True if can edit
 */
export const canEditResource = (user, resource) => {
  if (!user || !resource) return false;

  // Admin can edit anything
  if (isAdmin(user)) return true;

  // User can edit their own resources
  if (resource.createdBy === user.id) return true;

  // Manager can edit resources in their branch
  if (isManager(user) && resource.branchId === user.branchId) return true;

  return false;
};

/**
 * Check if user can delete a resource
 * @param {Object} user - User object
 * @param {Object} resource - Resource with createdBy
 * @returns {boolean} True if can delete
 */
export const canDeleteResource = (user, resource) => {
  if (!user || !resource) return false;

  // Only admin or owner can delete
  if (isAdmin(user)) return true;
  if (resource.createdBy === user.id) return true;

  return false;
};

export default {
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  getUserPermissions,
  canViewDashboard,
  canManageMembers,
  canApproveLoans,
  canDisburseLoans,
  canManageAccounting,
  isAdmin,
  isManager,
  isAccountant,
  isLoanOfficer,
  isTeller,
  isMember,
  canEditResource,
  canDeleteResource,
};
